// ✅ src/pages/VehicleV2/VehicleSpareDeductionListing.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/spareDeductionListing", spareDeductionListing);
//
// APIs used:
//   GET  https://nissi-india.com/api/spareDeductionListing/list?loginUserId=..&page=1&limit=10&search=..
//   POST https://nissi-india.com/api/spareDeductionListing/delete
//
// Routing:
//   Add : /vehicle/VehicleSpareDeductionEntry
//   Edit: /vehicle/VehicleSpareDeductionEntry?sparedeductionperid=123
//
// ✅ Updated to match VehicleTdsPercentEntry pattern:
// - common localStorage/sessionStorage header pattern
// - inventory headers passed in all API calls
// - loading + deleteLoading states
// - same UI structure style
// - server-side pagination
// - bulk delete supported

import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/spareDeductionListing";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const toStr = (v, def = "") => {
  if (v === undefined || v === null) return def;
  return String(v).trim();
};

const fmtDate = (v) => {
  const s = toStr(v);
  if (!s) return "";

  // already dd/MM/yyyy from backend
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const VehicleSpareDeductionListing = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ Shared localStorage / sessionStorage pattern
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || localStorage.getItem("branchid") || "";
  const branch = localStorage.getItem("branchType") || localStorage.getItem("branchtype") || "";
  const userId = localStorage.getItem("userId") || localStorage.getItem("loginuserid") || "";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // loading
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  const inputClass = `w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const pageBtnClass = (active = false, disabled = false) =>
    `px-4 py-2 rounded-lg border text-sm font-medium transition ${
      disabled
        ? "bg-gray-400 text-white cursor-not-allowed border-transparent"
        : active
        ? `${buttonClass} text-white border-transparent`
        : isDark
        ? "border-gray-700 text-gray-200 hover:bg-gray-800"
        : "border-gray-300 text-gray-700 hover:bg-gray-50"
    }`;

  const resetSelection = useCallback(() => {
    setSelectedIds([]);
    setSelectAll(false);
  }, []);

  const fetchList = useCallback(
    async (opts = {}) => {
      const nextPage = opts.page ?? page;
      const nextLimit = opts.limit ?? limit;
      const nextSearch = opts.search ?? search;

      try {
        setLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: {
            loginUserId,
            page: nextPage,
            limit: nextLimit,
            search: nextSearch,
          },
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(toInt(data?.total, 0));
        resetSelection();
      } catch (err) {
        console.error(err);
        setRows([]);
        setTotal(0);
        resetSelection();
        toast.error(err?.response?.data?.message || err?.message || "Server error while loading list");
      } finally {
        setLoading(false);
      }
    },
    [page, limit, search, loginUserId, commonHeaders, resetSelection]
  );

  useEffect(() => {
    fetchList();
  }, [page, limit, fetchList]);

  useEffect(() => {
    const idsOnPage = rows
      .map((r) => toInt(r.id || r.fld_sparedeductionperid))
      .filter((x) => x > 0);

    const allChecked =
      idsOnPage.length > 0 && idsOnPage.every((id) => selectedIds.includes(id));

    setSelectAll(allChecked);
  }, [rows, selectedIds]);

  const handleSearch = () => {
    setPage(1);
    fetchList({ page: 1, search });
  };

  const handleClearFilters = () => {
    setSearch("");
    setPage(1);
    fetchList({ page: 1, search: "" });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      resetSelection();
      return;
    }

    const idsOnPage = rows
      .map((r) => toInt(r.id || r.fld_sparedeductionperid))
      .filter((x) => x > 0);

    setSelectedIds(idsOnPage);
    setSelectAll(true);
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      toast.warn("Please select at least one record to delete");
      return;
    }

    try {
      setDeleteLoading(true);

      const { data } = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds,
          loginUserId,
        },
        {
          headers: commonHeaders,
        }
      );

      if (data?.success) {
        toast.success(data?.message || "Deleted Successfully");
      } else {
        toast.warn(data?.message || "Delete failed");
      }

      const inUse = Array.isArray(data?.inUseIds) ? data.inUseIds.length : 0;
      const failed = Array.isArray(data?.failedIds) ? data.failedIds.length : 0;

      if (inUse > 0) {
        toast.info(`${inUse} record(s) are in use and cannot be deleted`);
      }

      if (failed > 0) {
        toast.error(`${failed} record(s) failed to delete`);
      }

      const deletedCount = Array.isArray(data?.deletedIds) ? data.deletedIds.length : 0;
      const afterTotal = Math.max(0, total - deletedCount);
      const afterPages = Math.max(1, Math.ceil(afterTotal / limit));
      const nextPage = Math.min(page, afterPages);

      if (nextPage !== page) {
        setPage(nextPage);
      }

      fetchList({ page: nextPage });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Server error while deleting");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (row) => {
    const id = toInt(row.id || row.fld_sparedeductionperid);
    navigate(`/vehicle/VehicleSpareDeductionEntry?sparedeductionperid=${id}`);
  };

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE SPARE DEDUCTION PERCENT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-8">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Spare Deduction Listing
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {total} Records
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${showFilters ? "bg-gray-800" : ""}`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${showFilters ? "bg-gray-50" : ""}`
                }`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                type="button"
                onClick={() => navigate("/vehicle/VehicleSpareDeductionEntry")}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div
              className={`rounded-xl p-6 mb-6 border ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <FaFilter />
                FILTRATION AREA
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className={labelClass}>Search</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaSearch className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search subtype / date / percentage..."
                      className={`pl-10 ${inputClass}`}
                      disabled={loading || deleteLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  disabled={loading || deleteLoading}
                  className="text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 disabled:opacity-50"
                >
                  Clear All Filters
                </button>

                <button
                  type="button"
                  onClick={handleSearch}
                  disabled={loading || deleteLoading}
                  className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50`}
                >
                  <FaSearch />
                  Search Records
                </button>
              </div>
            </div>
          )}

          {/* Table */}
          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {loading ? (
              <div className="text-center py-16">
                <div className="inline-flex items-center gap-3">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                  <span className={`${isDark ? "text-gray-200" : "text-gray-700"}`}>
                    Loading records...
                  </span>
                </div>
              </div>
            ) : rows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaSearch className="w-10 h-10 text-gray-400" />
                </div>

                <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                  No Records Found
                </h3>

                <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Try adjusting your search or add a new record.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold">S.No</th>
                      <th className="px-4 py-4 text-center font-semibold">Date</th>
                      <th className="px-4 py-4 text-center font-semibold">Vehicle Sub Type</th>
                      <th className="px-4 py-4 text-center font-semibold">Deduction %</th>
                      <th className="px-4 py-4 text-center font-semibold">Edit</th>
                      <th className="px-4 py-4 text-center font-semibold">
                        <div className="flex items-center justify-center gap-2">
                          Select All
                          <input
                            type="checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            disabled={loading || deleteLoading || rows.length === 0}
                          />
                        </div>
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {rows.map((r, idx) => {
                      const id = toInt(r.id || r.fld_sparedeductionperid);

                      return (
                        <tr
                          key={id || idx}
                          className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                            isDark ? "bg-gray-900" : "bg-white"
                          }`}
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                            {r.slno ?? idx + 1}
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                            {fmtDate(r.fld_entrydate)}
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                            {r.fld_vehicle_subtypename || ""}
                          </td>

                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-white text-center">
                            {r.fld_percentage ?? ""}
                          </td>

                          <td className="px-4 py-3 text-center">
                            <button
                              type="button"
                              onClick={() => handleEdit(r)}
                              disabled={deleteLoading}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50"
                            >
                              <FaEdit />
                              Edit
                            </button>
                          </td>

                          <td className="px-4 py-3 text-center">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelect(id)}
                              disabled={deleteLoading}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination + Delete */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                className={pageBtnClass(false, page <= 1 || loading || deleteLoading)}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading || deleteLoading}
              >
                Prev
              </button>

              <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Page <b>{page}</b> / <b>{totalPages}</b>
              </span>

              <button
                type="button"
                className={pageBtnClass(false, page >= totalPages || loading || deleteLoading)}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading || deleteLoading}
              >
                Next
              </button>

              <div className="flex items-center gap-2 ml-2">
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>Rows:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setPage(1);
                    setLimit(toInt(e.target.value, 10));
                  }}
                  className={inputClass}
                  style={{ width: 120 }}
                  disabled={loading || deleteLoading}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleDelete}
                disabled={selectedIds.length === 0 || deleteLoading || loading}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  selectedIds.length === 0 || deleteLoading || loading
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                {deleteLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <FaTrash />
                    Delete Selected
                  </>
                )}
              </button>
            </div>
          </div>

          <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              INFRA SCREEN : VEHICLE SPARE DEDUCTION PERCENT
            </p>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSpareDeductionListing;